import { DateTime } from 'luxon';
import { averageTrainingLevels, getTrainingLevels } from '@cf-shared/data-model/training-level';
import { DeclaredState, FitnessState, ImmutableState, getWorkoutsPerWeek } from '@cf-shared/data-model/user-state';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { CouchLoginResult } from '@cf-shared/couch-auth';
import { UserLicenseType, isSubscriberInFreeTrial, isSubscriberTestPurchase } from '@cf-shared/data-model/couch-user';
import { isInGrace } from '@cf-shared/data-model/license-reconciler';
import { LocaleSettings } from '@cf-shared/shared-util';
import type { UiLanguageTelemetryProps } from 'i18n/ui-language';

// the notification toggles copied onto the profile. structurally compatible with
// SettingsService, so the service instance can be passed directly.
export interface ProfileNotificationSettings {
  notificationsWorkout: boolean;
  notificationsMeals: boolean;
  notificationsWeeklyCheckup: boolean;
  notificationsSleep: boolean;
}

export interface UserProfilePropsInput {
  immutable: ImmutableState;
  fitnessState: FitnessState;
  fitnessPlan: FitnessPlan;
  declaredState: DeclaredState;
  appDisplayMode: 'native' | 'standalone' | 'browser';
  notificationSettings?: ProfileNotificationSettings;
  // the raw UI-language signals (#4147): active app language, the device's raw
  // browser language, and the country-default language. Reported on the profile
  // (so they land at boot / login, not on every event), always — they exist
  // pre-intake, so they go in the base props. Computed by the caller with
  // buildUiLanguageTelemetryProps and passed in here to keep this pure.
  uiLanguage?: UiLanguageTelemetryProps;
  // injected so unit tests can pin the clock (age and plan-age math depend on it)
  now: DateTime;
}

// returns true when the user has finished the intake form. the birth date is the
// signal: intake's finish step (fillAndSaveImmutableState) is its only writer.
export function isIntakeDone(immutable: ImmutableState): boolean {
  return !!immutable.born;
}

// builds the Mixpanel people-profile properties for a user. before intake is done,
// only the props that exist without intake answers are returned (display mode +
// notification toggles) — the derived props would be junk (NaN brackets, empty
// levels). after intake, the full derived set is included. pure function so the
// two-phase behavior is unit-testable (see telemetry-profile-props.test.ts).
export function buildUserProfileProps(input: UserProfilePropsInput): { [key: string]: any } {
  const { immutable, fitnessState, fitnessPlan, declaredState, now } = input;

  const baseProps: { [key: string]: any } = {
    user_app_display_mode: input.appDisplayMode,
    // raw UI-language signals (#4147) — present even before intake, so they ride
    // on the base props and reach the profile at boot / login (see telemetry.md).
    ...(input.uiLanguage || {}),
    ...(input.notificationSettings && {
      user_notifications_workout: input.notificationSettings.notificationsWorkout,
      user_notifications_meals: input.notificationSettings.notificationsMeals,
      user_notifications_weekly_checkup: input.notificationSettings.notificationsWeeklyCheckup,
      user_notifications_sleep: input.notificationSettings.notificationsSleep,
    }),
  };

  // if intake is not finished yet, return only the base props — the caller sends
  // them now and runs this again once intake completes
  if (!isIntakeDone(immutable)) {
    return baseProps;
  }

  // ages and body fat go to Mixpanel as coarse brackets, not exact values, to keep
  // the profile low-resolution: age rounds up to the next 10-year step, body fat to
  // the next 5-point step
  const age = now.diff(DateTime.fromISO(immutable.born), 'years').years;
  const ageBracket = Math.ceil(age / 10) * 10;
  // if the user never measured body fat, 100 is the long-standing "unknown" marker
  // (reads as the top bracket in Mixpanel rather than a fake low value)
  const bf = (fitnessState.bfData && fitnessState.bfData.bf_pc) || 100;
  const bfBracket = Math.ceil(bf / 5) * 5;
  const level = averageTrainingLevels(Object.values(getTrainingLevels(declaredState.declaredStrength, fitnessPlan)));
  const goal = declaredState.trainingGoalGrowth;
  const workoutsPerWeek = getWorkoutsPerWeek(declaredState);
  const planAgeDays = fitnessPlan.ready
    ? Math.floor(now.diff(DateTime.fromISO(fitnessPlan.startPoint.timestamp_utc), 'days').days)
    : undefined;

  return {
    user_gender: immutable.gender,
    user_age_bracket: ageBracket,
    user_bf_bracket: bfBracket,
    user_training_level: level,
    user_training_goal: goal,
    user_workouts_per_week: workoutsPerWeek,
    user_training_dedication: declaredState.trainingDedicationLevel,
    user_nutrition_dedication: declaredState.nutritionDedicationLevel,
    user_nutrition_coaching: declaredState.nutritionCoaching,
    user_nutrition_tracking: declaredState.nutritionTracking,
    user_meal_frequency: declaredState.mealFrequency,
    ...(fitnessPlan.ready && {
      user_weekly_sets: fitnessPlan.trainingVolumeWeeklyAvg,
      user_body_comp_type: fitnessPlan.bodyCompositionType,
      user_plan_age_days: planAgeDays,
    }),
    ...baseProps,
  };
}

// the acquisition attribution forwarded to Refiner: the whole trackingData bag
// (utm_*, gclid/fbclid, referrer) minus client_user_agent. that field exists only for
// the server-side Meta CAPI match-quality score, not survey segmentation, and
// Refiner already knows the device via user_os / user_browser — so we drop it,
// the same exclusion getTrackingDataAsUrlParams makes. pure over its input: it
// clones first and never mutates the caller's bag, and returns {} when the bag
// is absent. kept here (next to buildUserProfileProps) so the exclusion behavior
// is unit-testable without instantiating TelemetryService.
export function buildRefinerAttributionTraits(trackingData: { [key: string]: any } | undefined): { [key: string]: any } {
  const attribution = { ...(trackingData || {}) };
  delete attribution.client_user_agent;
  return attribution;
}

// ---------------------------------------------------------------------------
// Refiner audience traits (#4325) — subscription, locale/country, food-log
// marker. Full rationale, and the rules about what must NOT also reach the
// Mixpanel people profile, live in design-docs/telemetry.md, section
// "Subscription, food-log and locale traits — Refiner only (#4325)".
// ---------------------------------------------------------------------------

/** What buildRefinerSubscriptionTraits reports. Either key is absent when unknown. */
export interface RefinerSubscriptionTraits {
  paid_subscription_active?: boolean;
  subscription_provider?: string;
}

/** What buildRefinerLocaleTraits reports. Every key is absent when unknown. */
export type RefinerLocaleTraits = { user_country?: string } & Partial<UiLanguageTelemetryProps>;

// the licence types that mean "a payment provider is billing this user". These
// enum values are already the exact strings the server writes to the Mixpanel
// `subscription_provider` property, so the Refiner trait matches it with no
// translation table. The rest (`key`, `coach`, `coachTier`, `coachSeat`) are not
// payment providers and report nothing.
const PAYMENT_PROVIDER_LICENSE_TYPES: ReadonlySet<UserLicenseType> = new Set([
  UserLicenseType.PayPro,
  UserLicenseType.Paddle,
  UserLicenseType.GooglePlay,
  UserLicenseType.Apple,
]);

/**
 * The subscription traits sent to Refiner so surveys can target paying users.
 * Pure over the login result, so it is unit-tested without standing up
 * TelemetryService — same reason buildRefinerAttributionTraits lives here.
 *
 * `paid_subscription_active` deliberately calls the three CANONICAL shared
 * predicates instead of restating their logic, so it cannot drift from the
 * server's daily recompute (`computeDesiredActive` in
 * syncSubscriptionActiveToMixpanel.ts). Two hand-written copies of the trial rule
 * had already drifted once — #4184 — so do not inline a fourth.
 *
 * Returns an EMPTY bag when there is no couch login: that is the offline /
 * deferred-login boot, where the answer is genuinely unknown. Reporting `false`
 * would brand a paying subscriber as non-paying every time they opened the app
 * offline; omitting leaves Refiner holding the last value it knew.
 */
export function buildRefinerSubscriptionTraits(
  couch: CouchLoginResult | null | undefined,
  now: DateTime,
): RefinerSubscriptionTraits {
  // if there is no couch login, we cannot answer either question
  if (!couch) {
    return {};
  }

  // has-access: the reconciled top-level status plus expiry, exactly as the
  // server asks it. `isInGrace` is an allow-list, so a paused Paddle
  // subscription correctly reads as no-access.
  const hasAccess = couch.subscriptionStatus !== undefined
    && isInGrace(couch.subscriptionStatus, couch.licenseExpires_utc, now.toMillis());

  // the trial rule wants the per-provider blocks. The login result carries the
  // Apple / Google / Paddle blocks as-is; PayPro's trial end arrives as a loose
  // top-level field, so it is handed over in the shape the rule reads.
  const isInFreeTrial = isSubscriberInFreeTrial({
    appleData: couch.appleData,
    googlePlayData: couch.googlePlayData,
    paddleData: couch.paddleData,
    payProData: { trialEnds_utc: couch.trialEnds_utc },
  }, now);

  const traits: RefinerSubscriptionTraits = {
    paid_subscription_active: hasAccess && !isInFreeTrial && !isSubscriberTestPurchase(couch),
  };

  // if a real payment provider backs this licence, name it. Reported whatever
  // the paying flag says — "which provider" and "are they paying right now" are
  // separate questions, and a cancelled Apple subscriber is still on Apple.
  if (couch.licenseType && PAYMENT_PROVIDER_LICENSE_TYPES.has(couch.licenseType)) {
    traits.subscription_provider = couch.licenseType;
  }

  return traits;
}

/**
 * The locale / country traits sent to Refiner, plus the UI-language traits passed
 * straight through so one spread carries the whole locale story.
 *
 * `user_country` is `locale.localeName`, which — despite the name — holds the
 * two-letter GeoIP country code, not a BCP-47 locale (see location.service.ts:
 * `localeName = ipInfo['country_code'] || 'US'`). It is the country the app
 * itself acts on for separators, clock format, week start and label style, so
 * reporting it keeps a survey audience and the app's own behaviour in agreement.
 *
 * Omitted while `localeName` is still empty, so Refiner is never told a country
 * before the app has one. Note that having one is not the same as having looked
 * it up successfully: `LocationService.fallbackToExistingLocale` sets `US` when
 * the GeoIP lookup fails on a first install, so a reported `US` may be that
 * fallback. It is still the country the app is behaving as, which is what this
 * trait is for.
 */
export function buildRefinerLocaleTraits(
  locale: LocaleSettings | undefined,
  uiLanguage: UiLanguageTelemetryProps | undefined,
): RefinerLocaleTraits {
  return {
    ...(locale?.localeName && { user_country: locale.localeName }),
    ...(uiLanguage || {}),
  };
}

/**
 * The telemetry events that mean "the user logged food", from issue #4325.
 *
 * The first time TelemetryService sees any of these it flips the sticky
 * `settings.hasLoggedFoodEver` marker, which becomes the `has_logged_food_ever`
 * Refiner trait. Kept here so the list lives in ONE place: a future sixth
 * food-logging path is covered the moment it emits one of these names.
 */
export const FOOD_LOGGED_EVENTS: readonly string[] = [
  'action_ingredient_add',
  // 'foods_log_food' still belongs here. #4967 removed the food list's per-card "+" button,
  // which was its only emitter, and moved the event to the portion screen's Log — the point
  // the food is actually written to the day. 'action_ingredient_quick_add' left this list for
  // good in the same change: nothing emits it any more, and a name nothing can emit reads as a
  // covered path.
  'foods_log_food',
  'meal_quick_macros',
  'day_items_log_macros_manually',
];

const FOOD_LOGGED_EVENT_SET: ReadonlySet<string> = new Set(FOOD_LOGGED_EVENTS);

/** True when this event name means the user logged food. See FOOD_LOGGED_EVENTS. */
export function isFoodLoggedEvent(eventName: string): boolean {
  return FOOD_LOGGED_EVENT_SET.has(eventName);
}

/** The state `shouldMarkFoodLoggedEver` decides on. */
export interface FoodLoggedMarkerInput {
  /** The telemetry event that just fired. */
  eventName: string;
  /** Whether `settings.hasLoggedFoodEver` is already set. */
  alreadyMarked: boolean;
  /** Whether a coach is currently managing someone else's account. */
  isManagingClient: boolean;
  /** Whether a signed-in user is available to attribute the marker to. */
  hasSignedInUser: boolean;
  /** Whether SettingsService has finished loading the user's stored settings. */
  settingsLoaded: boolean;
}

/**
 * Whether this telemetry event should flip the sticky food-log marker.
 *
 * Split out of TelemetryService so the rule is unit-testable — the service has no
 * test harness, and this is the one invariant worth pinning: the marker
 * transitions exactly once per user, ever.
 *
 * Four ways it says no, each for a different reason:
 *   - not a food-logging event, or the marker is already set — the ordinary case,
 *     and why this is cheap enough to ask on every single event;
 *   - a coach is managing a client — during management SettingsService holds the
 *     COACH's own non-program values and the signed-in user is still the coach,
 *     so marking would stamp the coach's profile for their client's food log and
 *     never credit the client. Permanently wrong, because the marker is sticky
 *     and the Mixpanel write is set-once;
 *   - settings not loaded yet — marking persists the WHOLE settings bag, so
 *     acting before the stored settings are in memory would write class defaults
 *     over the user's real preferences. This is the first caller driven by an
 *     event rather than a user gesture, so it is the first that could plausibly
 *     run that early;
 *   - no signed-in user — the Mixpanel and Refiner writes both need a uid, and
 *     since the transition happens only once per user ever, letting it burn here
 *     would lose those writes forever rather than merely delay them.
 *
 * The checks below are in the same order as this list.
 */
export function shouldMarkFoodLoggedEver(input: FoodLoggedMarkerInput): boolean {
  if (!isFoodLoggedEvent(input.eventName) || input.alreadyMarked) return false;
  if (input.isManagingClient) return false;
  if (!input.settingsLoaded) return false;
  return input.hasSignedInUser;
}

/**
 * Whether a signed-in user's arrival should emit `session_start_authenticated`.
 *
 * The auth state is published more than once per app start for the same user:
 * a warm resume emits the cached user, and a CouchDB login retry re-emits the
 * user it already emitted. Only the first emission for a user counts. A
 * different user in the same app run is a new session and counts again.
 *
 * `login` is not decided here. It fires only where a sign-in actually happens
 * (the login page and the one-time-token sign-in), never from the auth state.
 * See design-docs/telemetry.md, "Sign-in versus session start".
 */
export function shouldTrackAuthenticatedSessionStart(alreadyTrackedUid: string | undefined, uid: string): boolean {
  return alreadyTrackedUid !== uid;
}

/**
 * The extra Refiner traits that only exist once the user has finished intake and
 * has a ready plan (#4325).
 *
 * Split out so the gating rule is assertable: the profile `identifyUser` call is
 * NOT gated on intake any more (a pre-intake user must still receive identity,
 * device, attribution and audience traits), and only these extras are. That flip
 * is the largest behavioural change in #4325, so it should not live in prose
 * alone. Returns an empty bag before intake, which contributes nothing when spread.
 */
export function buildRefinerIntakeTraits(
  intakeFinished: boolean,
  fitnessPlan: FitnessPlan,
  userProperties: { [key: string]: any },
): { [key: string]: any } {
  // if intake is not finished, the derived engagement props do not exist yet
  if (!intakeFinished) {
    return {};
  }
  return {
    // the fitness plan starting point, as an ISO date
    user_intake_done_at: DateTime.fromISO(fitnessPlan.startPoint.timestamp_utc).toISODate(),
    ...userProperties,
  };
}
